import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Shield, FileText, RotateCcw, CheckCircle2, Calendar } from "lucide-react";
import api from "../../lib/api";
import { parseUtc } from '../../lib/datetime';

/**
 * AssignmentSummaryPanel — v4.0.1 "What Changed?"
 *
 * A calm, conversational forensic panel. NOT an audit console.
 *
 * Mounts as a right-side slide-over on desktop and as a bottom sheet
 * on mobile. Reads the normalized summary endpoint
 *   GET /api/operational-base/template-assignments/{id}/summary
 * and renders the executive_summary, grouped change_sections, and
 * protected_overrides as plain English/Français/Español sentences.
 *
 * Built so the same component can later evolve into a Replay UI:
 *   * sections mapped 1:1 to modules
 *   * change sentences already friendly
 *   * rollback CTA collocated with timeline
 * but no architectural over-build today.
 */
export const AssignmentSummaryPanel = ({ assignmentId, onClose, onRolledBack }) => {
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || "en").split("-")[0];
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rolling, setRolling] = useState(false);

    const fetchSummary = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/operational-base/template-assignments/${assignmentId}/summary`);
            setSummary(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.assignment_summary.fetch_error"));
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (assignmentId) fetchSummary(); }, [assignmentId]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const handleRollback = async () => {
        if (!window.confirm(t("ob.templates.rollback_confirm"))) return;
        setRolling(true);
        try {
            await api.post(`/operational-base/template-assignments/${assignmentId}/rollback`, {
                reason: "Restored from What Changed panel",
            });
            toast.success(t("ob.templates.rollback_success"));
            await fetchSummary();
            onRolledBack && onRolledBack();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.templates.rollback_error"));
        } finally {
            setRolling(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="assignment-summary-title"
            data-testid="ob-assignment-summary-panel"
            className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end"
            style={{ background: "rgba(0,0,0,0.55)" }}
            onClick={onClose}
        >
            <div
                role="document"
                className="ob-card w-full sm:w-[460px] sm:max-w-[92vw] max-h-[85vh] sm:max-h-none sm:h-full overflow-y-auto"
                style={{
                    background: "var(--ob-surface-elevated)",
                    padding: "1.75rem",
                    borderRadius: "1.25rem 1.25rem 0 0",
                    animation: "obFadeInDown 280ms ease-out",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-start justify-between gap-3 mb-5">
                    <div className="min-w-0 flex-1">
                        <p className="ob-eyebrow">{t("ob.assignment_summary.eyebrow")}</p>
                        <h2
                            id="assignment-summary-title"
                            className="text-xl mt-1"
                            style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}
                            data-testid="ob-assignment-summary-title"
                        >
                            {t("ob.assignment_summary.title")}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("common.close")}
                        className="p-1 rounded hover:bg-white/5 flex-shrink-0"
                        style={{ color: "var(--ob-text-tertiary)" }}
                        data-testid="ob-assignment-summary-close"
                    >
                        <X size={18} strokeWidth={1.5} aria-hidden="true" />
                    </button>
                </header>

                {loading || !summary ? (
                    <p className="text-sm" style={{ color: "var(--ob-text-tertiary)" }} data-testid="ob-assignment-summary-loading">
                        {loading ? t("ob.activity.loading") : t("ob.assignment_summary.fetch_error")}
                    </p>
                ) : (
                    <>
                        {/* Header card */}
                        <section
                            className="rounded-xl p-4 mb-5"
                            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                            data-testid="ob-assignment-summary-header"
                        >
                            <p className="text-base" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}>
                                {summary.template_name}
                                <span className="text-xs ml-2" style={{ color: "var(--ob-text-tertiary)" }}>
                                    {t("ob.templates.version_label", { v: summary.version_applied })}
                                </span>
                            </p>
                            <div className="mt-2 space-y-0.5 text-sm" style={{ color: "var(--ob-text-secondary)" }}>
                                {summary.applied_by_name && (
                                    <p>{t("ob.assignment_summary.applied_by", { name: summary.applied_by_name })}</p>
                                )}
                                <p className="inline-flex items-center gap-1.5">
                                    <Calendar size={11} strokeWidth={1.5} aria-hidden="true" />
                                    <span>{formatTimestamp(summary.applied_at, lang)}</span>
                                </p>
                                <p>{t("ob.assignment_summary.target_line", { name: summary.target_org_name })}</p>
                                <p style={{ color: "var(--ob-text-tertiary)" }}>
                                    {summary.scope === "subtree"
                                        ? t("ob.templates.scope_subtree")
                                        : t("ob.templates.scope_self_only")}
                                </p>
                            </div>
                        </section>

                        {/* Executive summary */}
                        <section className="mb-5" data-testid="ob-assignment-summary-exec">
                            <p className="ob-eyebrow mb-2">{t("ob.assignment_summary.exec_label")}</p>
                            <ul className="space-y-1.5 text-sm">
                                {(summary.executive_summary || []).map((line, i) => (
                                    <li
                                        key={i}
                                        className="inline-flex items-start gap-1.5"
                                        style={{ color: "var(--ob-text-secondary)" }}
                                    >
                                        <CheckCircle2 size={12} strokeWidth={1.5} style={{ color: "#A7F3D0", marginTop: 3 }} aria-hidden="true" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        {/* Change sections */}
                        {summary.no_changes ? (
                            <section
                                className="rounded-xl p-4 mb-5"
                                style={{ background: "rgba(167,243,208,0.06)", border: "1px solid rgba(167,243,208,0.16)" }}
                                data-testid="ob-assignment-summary-no-changes"
                            >
                                <p className="text-sm" style={{ color: "#A7F3D0" }}>
                                    {t("ob.assignment_summary.no_changes_heading")}
                                </p>
                                <p className="text-xs mt-1" style={{ color: "var(--ob-text-secondary)" }}>
                                    {t("ob.assignment_summary.no_changes_body")}
                                </p>
                            </section>
                        ) : (
                            <section className="mb-5">
                                <p className="ob-eyebrow mb-2">{t("ob.assignment_summary.changes_label")}</p>
                                <div className="space-y-3" data-testid="ob-assignment-summary-changes">
                                    {(summary.change_sections || []).map((sec, i) => (
                                        <div
                                            key={sec.module_name + i}
                                            className="rounded-xl p-3.5"
                                            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                                            data-testid={`ob-assignment-section-${sec.module_name}`}
                                        >
                                            <p className="text-sm font-medium inline-flex items-center gap-1.5" style={{ color: "var(--ob-text-primary)" }}>
                                                <FileText size={12} strokeWidth={1.5} aria-hidden="true" />
                                                <span>{(sec.module_display_i18n && sec.module_display_i18n[lang]) || sec.module_display}</span>
                                            </p>
                                            <ul className="mt-1.5 space-y-1 text-sm" style={{ color: "var(--ob-text-secondary)" }}>
                                                {(sec.sentences || []).map((s, j) => (
                                                    <li key={j}>· {s}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Protected overrides */}
                        {(summary.protected_overrides || []).length > 0 && (
                            <section className="mb-5">
                                <p className="ob-eyebrow mb-2 inline-flex items-center gap-1.5">
                                    <Shield size={11} strokeWidth={1.5} style={{ color: "#A7F3D0" }} aria-hidden="true" />
                                    {t("ob.assignment_summary.protected_label")}
                                </p>
                                <ul className="space-y-1.5 text-sm" data-testid="ob-assignment-summary-protected">
                                    {summary.protected_overrides.map((p, i) => (
                                        <li
                                            key={i}
                                            className="rounded-xl px-3 py-2"
                                            style={{ background: "rgba(167,243,208,0.05)", border: "1px solid rgba(167,243,208,0.14)", color: "var(--ob-text-secondary)" }}
                                        >
                                            {p.sentence}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Rollback section */}
                        <section
                            className="pt-4"
                            style={{ borderTop: "1px dashed rgba(255,255,255,0.08)" }}
                            data-testid="ob-assignment-summary-rollback-section"
                        >
                            {summary.rollback_eligible ? (
                                <>
                                    <p className="text-sm mb-3" style={{ color: "var(--ob-text-secondary)" }}>
                                        {t("ob.assignment_summary.rollback_eligible_message")}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={handleRollback}
                                        disabled={rolling}
                                        data-testid="ob-assignment-summary-rollback-btn"
                                        className="inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-2"
                                        style={{
                                            color: "#93C5FD",
                                            background: "rgba(59,130,246,0.07)",
                                            border: "1px solid rgba(59,130,246,0.20)",
                                            opacity: rolling ? 0.5 : 1,
                                            cursor: rolling ? "not-allowed" : "pointer",
                                        }}
                                    >
                                        <RotateCcw size={12} strokeWidth={1.5} aria-hidden="true" />
                                        {rolling ? t("ob.assignment_summary.rolling_back") : t("ob.assignment_summary.rollback_action")}
                                    </button>
                                </>
                            ) : summary.rolled_back_at ? (
                                <p className="text-sm" style={{ color: "var(--ob-text-secondary)" }}>
                                    {t("ob.assignment_summary.already_restored")}
                                </p>
                            ) : (
                                <p className="text-sm" style={{ color: "var(--ob-text-tertiary)" }}>
                                    {t("ob.assignment_summary.rollback_unavailable")}
                                </p>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

function formatTimestamp(iso, lang) {
    if (!iso) return "";
    try {
        const d = parseUtc(iso);
        return d.toLocaleString(lang || "en", {
            weekday: "long",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch (_) {
        return iso;
    }
}
